import { createContext, useContext, useEffect, useRef, useState, useCallback } from 'react'
import { supabase } from './supabase'

const Ctx = createContext(null)
export const useSession = () => useContext(Ctx)

/**
 * church_members is the source of truth for permissions — users.role is the old
 * enum and is deliberately ignored here.
 */
export function SessionProvider({ children }) {
  const [session, setSession] = useState(null)
  const [profile, setProfile] = useState(null)
  const [memberships, setMemberships] = useState([])
  const [subscription, setSubscription] = useState(null)
  const [activeChurchId, setActiveChurchId] = useState(
    () => localStorage.getItem('beseter.church') || null
  )
  // Readiness is "the profile in state belongs to the signed-in user", not a
  // loading flag. Auth transitions overlap (SIGNED_IN, then TOKEN_REFRESHED),
  // and a boolean can read false in the gap while profile is still null —
  // which the router mistakes for "this user has no profile yet".
  const [loadedFor, setLoadedFor] = useState(undefined)
  const [aal, setAal] = useState(null)
  const seq = useRef(0)

  const checkAal = useCallback(async () => {
    try {
      const { data } = await supabase.auth.mfa.getAuthenticatorAssuranceLevel()
      setAal(data ?? null)
    } catch {
      setAal(null) // fail open — never gate the whole app on this check erroring
    }
  }, [])

  const load = useCallback(async (uid) => {
    const ticket = ++seq.current
    if (!uid) {
      if (ticket === seq.current) {
        setProfile(null); setMemberships([]); setSubscription(null); setLoadedFor(null)
      }
      return
    }
    const [{ data: me }, { data: mems }, { data: sub }] = await Promise.all([
      supabase.from('users').select('*').eq('id', uid).maybeSingle(),
      supabase
        .from('church_members')
        .select('*, churches(id, name, city, state, require_senior_signoff, show_writers_full_trail, allow_voice_memo_only, two_sunday_test, process_configured)')
        .eq('user_id', uid),
      supabase.from('subscriptions').select('*').eq('user_id', uid).maybeSingle(),
    ])
    if (ticket !== seq.current) return // a newer load has already landed
    setProfile(me ?? null)
    setMemberships(mems ?? [])
    setSubscription(sub ?? null)
    setLoadedFor(uid)
  }, [])

  useEffect(() => {
    let alive = true
    supabase.auth.getSession().then(async ({ data }) => {
      if (!alive) return
      setSession(data.session)
      await Promise.all([load(data.session?.user?.id), checkAal()])
    })
    const { data: sub } = supabase.auth.onAuthStateChange(async (_e, s) => {
      setSession(s)
      await Promise.all([load(s?.user?.id), checkAal()])
    })
    return () => { alive = false; sub.subscription.unsubscribe() }
  }, [load, checkAal])

  const refresh = useCallback(() => load(session?.user?.id), [load, session])

  // Staff seats outrank a songwriter seat when someone holds both.
  const rank = { reviewer: 3, admin: 2, songwriter: 1 }
  const approved = memberships.filter((m) => m.membership_status === 'approved')
  const sorted = [...approved].sort((a, b) => rank[b.role] - rank[a.role])
  const active =
    approved.find((m) => m.church_id === activeChurchId) || sorted[0] || null

  const chooseChurch = useCallback((id) => {
    setActiveChurchId(id)
    if (id) localStorage.setItem('beseter.church', id)
    else localStorage.removeItem('beseter.church')
  }, [])

  // Mirrors subscription_tier() in the database: paused or canceled reads as
  // free, immediately — there's no grace period to honour without a billing
  // provider behind it. See subscription itself for the raw row (used by the
  // plans screen to tell "paused" apart from "never subscribed").
  const tier = subscription && ['active', 'past_due'].includes(subscription.status)
    ? subscription.tier : 'free'

  const value = {
    session,
    user: session?.user ?? null,
    profile,
    memberships,
    subscription,
    tier,
    isPlus: tier === 'plus',
    approved,
    active,
    church: active?.churches ?? null,
    role: active?.role ?? null,
    isAdmin: active?.role === 'admin',
    isReviewer: active?.role === 'reviewer',
    isSongwriter: active?.role === 'songwriter',
    // Platform staff, not church staff — the one role that does NOT come from
    // church_members, because it is not scoped to a church. It steers a link
    // and nothing more: every superadmin surface is gated again by
    // is_superadmin() in the database, which reads this same column server-side
    // where the browser cannot reach it.
    isSuperadmin: profile?.role === 'superadmin',
    // Platform staff who hold no church seat. <Platform> already says it —
    // "a superadmin is not a member of anything, they run the platform" — but
    // nothing acted on it, so `role` came back null, tabsForRole(null) fell
    // through to SONGWRITER_TABS, and Gate sent them to /home. Staff got a
    // songwriter's app: a Songs tab over an empty catalog, a "+ Song" button,
    // and a Church tab pointing at /join, inviting our own staff to apply to
    // a church.
    //
    // Deliberately `&& !active`, not a bare superadmin check. Holding both is
    // legitimate — a staff member who also writes at their own church — and
    // that account should keep its seat's whole surface and simply gain the
    // moderation door. This flag is only for the seatless case, which is the
    // one that was falling back to songwriter-by-default.
    isPlatformOnly: profile?.role === 'superadmin' && !active,
    // Not ready until the loaded profile matches whoever is signed in.
    loading: loadedFor === undefined || loadedFor !== (session?.user?.id ?? null),
    // true only for the gap between "signed in with a verified TOTP factor"
    // and "actually typed today's code" — aal2 is the level a factor demands,
    // not one the session carries by default the moment a factor exists.
    needsMfaChallenge: !!aal && aal.nextLevel === 'aal2' && aal.currentLevel !== aal.nextLevel,
    recheckMfa: checkAal,
    refresh,
    chooseChurch,
    signOut: async () => {
      await supabase.auth.signOut()
      localStorage.removeItem('beseter.church')
      // Welcome reads this after signup to pick /church/new over /join. Left
      // behind, it steers whoever signs up next on this browser.
      localStorage.removeItem('beseter.intent')
    },
  }
  return <Ctx.Provider value={value}>{children}</Ctx.Provider>
}
